<script setup lang="ts">
import {
    ExtensionProfitabilityTabHeader,
    ExtensionProfitabilityTabRow
} from "@/extension/components/extension-profitability-tab";
import {useSubscriptionPlanStore} from "@/stores/subscription-plan.store";
import {useExtensionStore} from "@/stores/extension.store";
import {computed, onMounted, ref, watch} from "vue";
import {subscriptionPlanApi} from "@/container";
import {ShowcaseItem, ShowcaseType} from "@/core/interfaces/showcase";
import {DotLottieVue} from "@lottiefiles/dotlottie-vue";
import {Label} from "@/core/components/ui/label";
import {Switch} from "@/core/components/ui/switch";
import {SubscriptionPlan, SubscriptionPlanShowcase} from "@/subscription-plan/interfaces";
import {Loader2} from '@lucide/vue';
import Extension from "@/core/interfaces/extension/Extension";
import {useRoute, useRouter} from "vue-router";
import ExtensionNavigationService from "@/extension/services/ExtensionNavigationService";
import useTaskResult from "@/core/composables/task-result/useTaskResult";

const {individualPlans, globalPlan} = useSubscriptionPlanStore();
const {extensionsBySubscriptionPlan} = useExtensionStore();
const extensionShowcases = ref<Record<number, ShowcaseItem[]>>({});
const loadingEnable = ref({});
const router = useRouter();
const route = useRoute();
const margins = ref<Record<number, number>>({});
const {loading, startTask, result} = useTaskResult<SubscriptionPlanShowcase[]>();

const handleExtensionClick = (extension: Extension) => {
    ExtensionNavigationService.showExtensionDetails(extension.id, router, route);
};

onMounted(() => {
    if(globalPlan) {
        startTask({
            runTask: () => subscriptionPlanApi().computeShowcases(globalPlan.id),
            pollTask: (taskId: number) => subscriptionPlanApi().showcasesStatus(globalPlan.id, taskId)
        });
    }
});

watch(() => result.value, (showcases) => {
    if(showcases) {
        showcases.forEach((data) => {
            if (!extensionShowcases.value[data.extension.id]) {
                extensionShowcases.value[data.extension.id] = [];
            }

            extensionShowcases.value[data.extension.id] = data.showcases;
        });
    }
}, {immediate: true});

/**
 * Get the extensions for a specific subscription plan.
 *
 * @param subscriptionPlanId
 */
const extensions = (subscriptionPlanId: number) => {
    return extensionsBySubscriptionPlan(subscriptionPlanId);
};

const totalProfitability = computed(() => {
    return Object.keys(margins.value).reduce((total, key) => {
        const rowProfitability = margins.value[Number(key)] ?? 0;

        return total + rowProfitability;
    }, 0);
});

/**
 * Enable the extensions that belongs to a specific subscription plan.
 */
const enableSubscriptionPlan = (plan: SubscriptionPlan) => {
    loadingEnable.value[plan.id] = true;
    subscriptionPlanApi()
        .enableExtensions(plan.id)
        .then(() => {
            const extensions = extensionsBySubscriptionPlan(plan.id);

            extensions.forEach(extension => {
                extension.enabled = true;
            });
        })
        .finally(() => {
            loadingEnable.value[plan.id] = false;
        });
};

/**
 * Disable the extensions that belongs to a specific subscription plan.
 */
const disableSubscriptionPlan = (plan: SubscriptionPlan) => {
    loadingEnable.value[plan.id] = true;
    subscriptionPlanApi()
        .disableExtensions(plan.id)
        .then(() => {
            const extensions = extensionsBySubscriptionPlan(plan.id);

            extensions.forEach(extension => {
                extension.enabled = false;
            });
        })
        .finally(() => {
            loadingEnable.value[plan.id] = false;
        });
};

/**
 * Toggle the extensions for a specific subscription plan.
 */
const toggleExtensions = (plan: SubscriptionPlan) => {
    if (allExtensionsEnabled(plan)) {
        disableSubscriptionPlan(plan);
    } else {
        enableSubscriptionPlan(plan);
    }
};

/**
 * Check if all extensions for a specific subscription plan are enabled.
 */
const allExtensionsEnabled = (plan: SubscriptionPlan) => {
    const extensions = extensionsBySubscriptionPlan(plan.id);
    return extensions.every(extension => extension.enabled);
};

const totalMargin = computed(() => {
    return Object.values(extensionShowcases.value)
        .map(showcases => {
            return showcases.filter(showcase => showcase.type === ShowcaseType.PROFITABILITY);
        })
        .flat()
        .reduce((total, showcase) => {
            return total + ((showcase.value as number) ?? 0);
        }, 0);
});
</script>

<template>
  <div class="space-y-8 pb-8">
    <div class="flex justify-between px-6 py-2 bg-secondary-lightest items-center">
      <div>
        <h1 class="text-2xl font-content-bold">
          Estimez vos revenus potentiels
        </h1>
        <p>Simulez vos gains en fonction du potentiel à réaliser sur chaque module !</p>
      </div>

      <img src="@/assets/images/happy_calculator.png" alt="calculator" class="h-20 object-contain -rotate-12">
    </div>

    <div v-if="loading" class="flex flex-col justify-center items-center text-center">
      <DotLottieVue src="animations/profit-calculator.lottie" class="w-1/2 aspect-square" loop autoplay />
      <h1>Veuillez patienter, nous calculons la rentabilité pour chaque module</h1>
    </div>

    <div v-else class="space-y-4">
      <div class="sticky-header px-6 py-1 bg-gray-100">
        <ExtensionProfitabilityTabHeader :current-profit="totalProfitability" :total-profit="totalMargin" />
        <p class="text-sm text-primary mt-1">
          👉 Par défaut, l'estimation correspond au taux moyen de réalisation du potentiel observé la première année.
        </p>
      </div>

      <div class="px-6 space-y-8">
        <div v-for="plan in (individualPlans ?? [])" :key="plan.id" class="space-y-4">
          <div>
            <div class="flex justify-between">
              <h2 class="text-black">
                {{ plan.name }}
              </h2>
              <div class="flex items-baseline gap-1 pr-4">
                <Label>
                  Activer tous les modules {{ plan.name }}
                </Label>
                <Switch
                  :model-value="allExtensionsEnabled(plan)" :disabled="loadingEnable[plan.id]"
                  @update:model-value="toggleExtensions(plan)"
                >
                  <template v-if="loadingEnable[plan.id]" #thumb>
                    <Loader2 class="w-4 h-4 text-primary animate-spin" />
                  </template>
                </Switch>
              </div>
            </div>
            <p v-if="plan.sales_pitch" class="text-sm">
              {{ plan.sales_pitch }}
            </p>
          </div>
          <div v-for="extension in extensions(plan.id)" :key="extension.id">
            <ExtensionProfitabilityTabRow
              :default-items="extensionShowcases[extension.id] ?? []"
              :extension="extension"
              @extension-click="handleExtensionClick"
              @margin-change="value => margins[extension.id] = value"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.sticky-header {
    position: sticky;
    top: 0;
    z-index: 50;
}
</style>
